import React from 'react';
import { useNavigate } from 'react-router-dom';
import Badge from '../../../components/common/Badge/Badge';
import Button from '../../../components/common/Button/Button';
import InfoCard from '../../../components/common/InfoCard/InfoCard';
import Pagination from '../../../components/common/Pagination/Pagination';
import { getStudentInactivationReasonLabel } from '../../../constants/studentInactivationReason';
import { useClientPagination } from '../../../hooks/useClientPagination';
import { PAYMENT_STATUS_LABELS, getPaymentMethodLabel } from '../../../types/orchestrator';
import type {
  DashboardSupportCancellationRow,
  DashboardSupportPaymentRow,
  DashboardSupportStudentRow,
} from '../../../types/dashboard';
import { formatCurrency, formatDate, formatDateTime } from '../../../utils';
import styles from './SupportLists.module.css';

export const studentPaymentsPath = (studentId: number): string => (
  `/alunas/${studentId}/editar?section=payments`
);

const SUPPORT_LIST_PAGE_SIZE = 20;

const openStudent = (navigate: ReturnType<typeof useNavigate>, studentId: number) => {
  navigate(`/alunas/${studentId}/editar`);
};

interface SupportListCardProps<T> {
  title: string;
  items: T[];
  emptyMessage: string;
  children: (pageItems: T[]) => React.ReactNode;
}

export function SupportListCard<T>({
  title,
  items,
  emptyMessage,
  children,
}: SupportListCardProps<T>) {
  const { pageItems, pagination, setPage } = useClientPagination(items, SUPPORT_LIST_PAGE_SIZE);

  return (
    <InfoCard title={`${title} (${items.length})`} className={styles.card}>
      {items.length === 0 ? (
        <p className={styles.empty}>{emptyMessage}</p>
      ) : (
        <>
          <div className={styles.listBody}>{children(pageItems)}</div>
          {pagination.totalPages > 1 && (
            <Pagination
              pagination={pagination}
              onPageChange={setPage}
            />
          )}
        </>
      )}
    </InfoCard>
  );
}

const StudentIdentity: React.FC<{
  studentId: number;
  studentName: string;
  coachName: string;
}> = ({ studentId, studentName, coachName }) => {
  const navigate = useNavigate();

  return (
    <button
      type="button"
      className={styles.identity}
      onClick={() => openStudent(navigate, studentId)}
    >
      <span className={styles.avatar} aria-hidden="true">
        {studentName.charAt(0)}
      </span>
      <span className={styles.details}>
        <strong>{studentName}</strong>
        <span>{coachName}</span>
      </span>
    </button>
  );
};

interface StudentStackProps {
  rows: DashboardSupportStudentRow[];
  meta: (row: DashboardSupportStudentRow) => string;
  badge?: (row: DashboardSupportStudentRow) => React.ReactNode;
  action?: (row: DashboardSupportStudentRow) => React.ReactNode;
}

export const StudentStack: React.FC<StudentStackProps> = ({ rows, meta, badge, action }) => (
  <ul className={styles.stack}>
    {rows.map((row) => (
      <li key={row.studentId}>
        <StudentIdentity
          studentId={row.studentId}
          studentName={row.studentName}
          coachName={row.coachName}
        />
        <div className={styles.meta}>
          <span>{meta(row)}</span>
          {badge?.(row)}
        </div>
        {action ? <div className={styles.actions}>{action(row)}</div> : null}
      </li>
    ))}
  </ul>
);

export const formatOptionalDate = (value: string | null | undefined): string => {
  if (!value) {
    return '—';
  }

  return value.length > 10 ? formatDateTime(value) : formatDate(value);
};

export const formatReason = (value: string | null | undefined): string => (
  getStudentInactivationReasonLabel(value)
);

interface PaymentTableProps {
  rows: DashboardSupportPaymentRow[];
}

export const PaymentTable: React.FC<PaymentTableProps> = ({ rows }) => {
  const navigate = useNavigate();

  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Aluna</th>
            <th>Professora</th>
            <th>Vencimento</th>
            <th>Forma</th>
            <th>Valor</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={`${row.paymentHistoryId}-${row.studentId}`}>
              <td>
                <button
                  type="button"
                  className={styles.nameLink}
                  onClick={() => navigate(studentPaymentsPath(row.studentId))}
                >
                  {row.studentName}
                </button>
              </td>
              <td>{row.coachName}</td>
              <td className={styles.nowrap}>{formatOptionalDate(row.dueDate)}</td>
              <td>{getPaymentMethodLabel(row.paymentMethod)}</td>
              <td>{row.value == null ? '—' : formatCurrency(row.value)}</td>
              <td>
                <Badge variant={row.status.toUpperCase() === 'PENDING' ? 'warning' : 'danger'}>
                  {PAYMENT_STATUS_LABELS[row.status.toUpperCase()] ?? row.status}
                </Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

interface CancellationTableProps {
  rows: DashboardSupportCancellationRow[];
}

export const CancellationTable: React.FC<CancellationTableProps> = ({ rows }) => {
  const navigate = useNavigate();

  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Professora</th>
            <th>Aluna</th>
            <th>Data</th>
            <th>Motivo</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td>{row.coachName}</td>
              <td>
                <button
                  type="button"
                  className={styles.nameLink}
                  onClick={() => openStudent(navigate, row.studentId)}
                >
                  {row.studentName}
                </button>
              </td>
              <td>{formatOptionalDate(row.canceledAt)}</td>
              <td>{formatReason(row.reason)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

interface ConfirmPaymentButtonProps {
  studentId: number;
}

export const ConfirmPaymentButton: React.FC<ConfirmPaymentButtonProps> = ({ studentId }) => {
  const navigate = useNavigate();

  return (
    <Button
      type="button"
      variant="outline"
      size="xs"
      icon="payments"
      onClick={() => navigate(studentPaymentsPath(studentId))}
    >
      Confirmar pagamento
    </Button>
  );
};
